/**
 * HandCardTile - One card in a simulated opening hand
 *
 * Doubles as the bottoming picker: while bottoming, clicking toggles selection
 * instead of opening card details.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { IoBan, IoCheckmark } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import type { SimCard } from '@/features/decks/lib/handSim';

import SimCardArt from './SimCardArt';

// ============================================================================
// Types
// ============================================================================

export interface HandCardTileProps {
  card: SimCard;
  /** Earliest turn this card can be cast, null when the hand can never pay for it */
  castableTurn: number | null;
  /** Bottoming mode: click toggles selection rather than opening details */
  selectable: boolean;
  selected: boolean;
  /** Bottoming selection is already full — this tile can't be added */
  disabled?: boolean;
  onToggleSelect: (key: string) => void;
  onOpenDetails: (card: SimCard) => void;
  /** Short caption rendered under the tile (used for commander cast turns) */
  annotation?: string;
}

// ============================================================================
// Component
// ============================================================================

export default function HandCardTile({
  card,
  castableTurn,
  selectable,
  selected,
  disabled = false,
  onToggleSelect,
  onOpenDetails,
  annotation,
}: HandCardTileProps): ReactElement {
  const uncastable = !card.isLand && castableTurn === null;

  const label = selectable
    ? `${selected ? 'Unselect' : 'Select'} ${card.name} to put on the bottom`
    : `Open details for ${card.name}${uncastable ? ' (not castable from this hand)' : ''}`;

  return (
    <CardHoverPreview imageUris={card.imageUris} name={card.name}>
      <div>
        <button
          type="button"
          onClick={() => (selectable ? onToggleSelect(card.key) : onOpenDetails(card))}
          disabled={disabled}
          aria-pressed={selectable ? selected : undefined}
          aria-label={label}
          title={disabled ? 'Already picked enough cards to bottom' : undefined}
          className={clsx(
            'relative block w-full overflow-hidden rounded shadow-md transition-transform',
            'aspect-488/680 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
            !disabled && 'hover:scale-105',
            disabled && 'cursor-not-allowed opacity-60',
            card.isLand && 'ring-2 ring-emerald-500/50',
            card.isManaRock && 'ring-2 ring-amber-400/50',
            selected && 'ring-2 ring-rose-400 brightness-50',
            uncastable && !selected && 'brightness-50',
          )}
        >
          <SimCardArt card={card} />

          {uncastable && !selected && (
            <span
              className="absolute top-0.5 right-0.5 rounded-full bg-zinc-950/85 p-0.5 text-rose-400"
              title="Not castable from this hand"
            >
              <IoBan className="h-3 w-3" />
            </span>
          )}

          {selected && (
            <span className="absolute top-0.5 right-0.5 rounded-full bg-rose-500 p-0.5 text-white">
              <IoCheckmark className="h-3 w-3" />
            </span>
          )}
        </button>
        {annotation && <div className="text-ash mt-0.5 text-center text-[10px]">{annotation}</div>}
      </div>
    </CardHoverPreview>
  );
}
